// Who is signed in. The server session (cookie) is the source of truth;
// /api/auth-check tells us whether it is still valid.

import { api, onUnauthorized, ApiError } from '../api';
import { router } from '../router.svelte';
import { prefs } from '../prefs.svelte';
import { i18n, type Lang } from '../i18n.svelte';
import { native } from '../native';
import type { SessionUser } from '../types';

interface LoginResult {
  success?: boolean;
  requiresMfa?: boolean;
  message?: string;
  language?: Lang | null;
  user?: SessionUser;
  account_created?: boolean;
}

const PUBLIC_PREFIXES = ['/login', '/register', '/forgot-password', '/reset-password', '/privacy', '/voorwaarden', '/cookies'];

class Session {
  user = $state<SessionUser | null>(null);
  checked = $state(false);
  private signedOutListeners = new Set<() => void>();
  private signedInListeners = new Set<() => void>();

  constructor() {
    onUnauthorized(() => {
      if (!this.user) return;
      this.clearLocal();
      this.user = null;
      if (router.path.startsWith('/app')) {
        router.navigate(`/login?next=${encodeURIComponent(router.path + router.search)}&expired=1`, { replace: true });
      }
    });
  }

  get signedIn() {
    return !!this.user;
  }

  onSignedIn(fn: () => void) {
    this.signedInListeners.add(fn);
    return () => this.signedInListeners.delete(fn);
  }

  onSignedOut(fn: () => void) {
    this.signedOutListeners.add(fn);
    return () => this.signedOutListeners.delete(fn);
  }

  async check(): Promise<boolean> {
    try {
      const res = await api.get<{ authenticated: boolean; user?: SessionUser }>('/api/auth-check', { allowUnauthorized: true, retries: 4 });
      if (res.authenticated && res.user) {
        const wasSignedIn = !!this.user;
        this.user = res.user;
        if (!wasSignedIn) this.signedInListeners.forEach((fn) => fn());
      } else {
        this.user = null;
      }
    } catch (err) {
      // 401 = signed out. Anything else (server busy, offline): keep the user on
      // the page; the API calls will retry.
      if (err instanceof ApiError && err.status === 401) this.user = null;
      else if (!this.user) this.user = null;
    } finally {
      this.checked = true;
    }
    return !!this.user;
  }

  private afterLogin(res: LoginResult) {
    if (res.language && ['nl', 'en', 'de', 'fr'].includes(res.language)) {
      prefs.update({ language: res.language });
      i18n.set(res.language);
    }
    if (res.user) {
      this.user = { ...res.user, provider: res.user.provider ?? null, email: res.user.email ?? null, name: res.user.name ?? null };
      this.signedInListeners.forEach((fn) => fn());
      // The login response lacks the onboarding flag; auth-check has it.
      this.check().catch(() => {});
    }
  }

  async login(email: string, password: string): Promise<LoginResult> {
    const res = await api.post<LoginResult>('/api/login', { email, password }, { allowUnauthorized: true });
    if (res.success && !res.requiresMfa) this.afterLogin(res);
    return res;
  }

  async verifyMfa(code: string): Promise<LoginResult> {
    const res = await api.post<LoginResult>('/api/login/verify-mfa', { code }, { allowUnauthorized: true });
    if (res.success) this.afterLogin(res);
    return res;
  }

  async googleLogin(credential: string): Promise<LoginResult> {
    const res = await api.post<LoginResult>('/api/google-auth', { credential }, { allowUnauthorized: true });
    if (res.success || (res as { authenticated?: boolean }).authenticated) {
      await this.check();
      this.afterLogin({ language: res.language });
    }
    return res;
  }

  /** Marks the session as signed in after e-mail verification polling. */
  async adoptServerSession() {
    await this.check();
  }

  async logout() {
    try {
      if (native.isNativeApp()) {
        const token = await native.getDeviceToken().catch(() => null);
        if (token) await api.del('/api/notification-tokens', { token }).catch(() => {});
        await native.signalLogout().catch(() => {});
      }
      await api.post('/api/auth-logout');
    } catch { /* signed out locally regardless */ }
    this.clearLocal();
    this.user = null;
    prefs.signedIn = false;
    this.signedOutListeners.forEach((fn) => fn());
    router.navigate('/login', { replace: true });
  }

  private clearLocal() {
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith('tp.cache.') || key === 'tp.timer' || key === 'tp.queue') localStorage.removeItem(key);
      }
    } catch { /* ignore */ }
  }

  isPublicPath(path: string) {
    return path === '/' || PUBLIC_PREFIXES.some((p) => path === p || path.startsWith(p + '/'));
  }
}

export const session = new Session();
